import { useState } from 'react';
import type { PlaceDto } from '../../shared/api';
import { fmtQty } from '../../shared/domain';
import { del, patch, post, refresh } from '../api';
import { Err, Modal, errMsg, pl, useApp } from '../components/ui';
import { NewItemDialog } from '../components/dialogs';

const units = (n: number) => `${fmtQty(n)} unit${n === 1 ? '' : 's'}`;

interface Spot {
  place: PlaceDto;
  shelfId: number | null;
  label: string;
}

/** What is on one shelf (or in a place without a shelf): warehouse stock and units assigned to projects. */
function useContents() {
  const { d } = useApp();
  return (placeId: number, shelfId: number | null) => {
    const stock = d.b.items.filter((i) => i.placeId === placeId && i.shelfId === shelfId && i.qty > 0);
    const assigned = d.allocs.filter((a) => a.placeId === placeId && a.shelfId === shelfId);
    return { stock, assigned, rows: stock.length + assigned.length, units: stock.reduce((n, i) => n + i.qty, 0) + assigned.reduce((n, a) => n + a.qty, 0) };
  };
}

function ShelfDialog({ spot, onClose }: { spot: Spot; onClose: () => void }) {
  const { d, openItem, openAdd, openTake, openReturn } = useApp();
  const [adding, setAdding] = useState(false);
  const c = useContents()(spot.place.id, spot.shelfId);
  return (
    <Modal
      title={spot.label}
      onClose={onClose}
      wide
      foot={
        d.isAdmin && (
          <button className="btn" onClick={() => setAdding(true)}>
            + New item here
          </button>
        )
      }
    >
      {!c.rows ? (
        <p className="meta">Nothing here.</p>
      ) : (
        <div className="scroll">
          <table className="mini">
            <thead>
              <tr>
                <th>Item</th>
                <th>QTY here</th>
                <th>For</th>
                <th>Type</th>
                <th>QC</th>
                {d.isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {c.stock.map((i) => (
                <tr key={`i${i.id}`}>
                  <td>
                    <button className="linkbtn" onClick={() => openItem(i.id)}>
                      <b>{i.name}</b>
                    </button>
                  </td>
                  <td>
                    <b>{fmtQty(i.qty)}</b>
                  </td>
                  <td>{i.project ? i.project : <span className="meta">Warehouse</span>}</td>
                  <td>{i.type || '–'}</td>
                  <td>{i.qc}</td>
                  {d.isAdmin && (
                    <td className="nowrap">
                      <button className="btn small" onClick={() => openAdd(i.id)}>
                        + Add
                      </button>{' '}
                      <button className="btn small" onClick={() => openTake(i.id)}>
                        − Take
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {c.assigned.map((a) => (
                <tr key={`a${a.id}`}>
                  <td>
                    <button className="linkbtn" onClick={() => openItem(a.item.id)}>
                      <b>{a.item.name}</b>
                    </button>
                  </td>
                  <td>
                    <b>{fmtQty(a.qty)}</b>
                  </td>
                  <td>
                    <span className="tag soft">{a.project}</span> <span className="meta">{a.installation.toLowerCase()}</span>
                  </td>
                  <td>{a.item.type || '–'}</td>
                  <td>{a.qc}</td>
                  {d.isAdmin && (
                    <td>
                      <button className="btn small" onClick={() => openReturn(a.id)}>
                        Return
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && <NewItemDialog preset={{ placeId: spot.place.id, shelfId: spot.shelfId }} onClose={() => setAdding(false)} />}
    </Modal>
  );
}

function PlaceEditor({ place, onClose }: { place?: PlaceDto; onClose: () => void }) {
  const { toast } = useApp();
  const [name, setName] = useState(place?.name ?? '');
  const [shelfText, setShelfText] = useState('Shelf 1\nShelf 2\nShelf 3');
  const [newShelf, setNewShelf] = useState('');
  const [err, setErr] = useState('');
  const act = async (fn: () => Promise<unknown>, msg: string, close = false) => {
    setErr('');
    try {
      await fn();
      await refresh();
      toast(msg);
      if (close) onClose();
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  if (!place) {
    const shelves = shelfText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    return (
      <Modal
        title="New place"
        onClose={onClose}
        foot={
          <button className="btn primary" disabled={!name.trim()} onClick={() => void act(() => post('/api/places', { name, shelves }), `${name} added`, true)}>
            Save place
          </button>
        }
      >
        <div className="fgrid one">
          <label className="field">
            Name of the place (must be unique) *
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. IKEA 1" />
          </label>
          <label className="field">
            Shelves, one name per line (a number or any name, e.g. a project)
            <textarea rows={5} value={shelfText} onChange={(e) => setShelfText(e.target.value)} />
          </label>
        </div>
        <Err e={err} />
      </Modal>
    );
  }
  return (
    <Modal
      title={`Edit ${place.name}`}
      onClose={onClose}
      foot={
        <button className="btn danger" onClick={() => confirm(`Delete ${place.name} and its shelves? Only possible when it is empty.`) && void act(() => del(`/api/places/${place.id}`), `${place.name} deleted`, true)}>
          Delete place
        </button>
      }
    >
      <div className="row">
        <label className="field grow">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <button className="btn" disabled={!name.trim() || name === place.name} onClick={() => void act(() => patch(`/api/places/${place.id}`, { name }), 'Place renamed')}>
          Rename
        </button>
      </div>
      <h4>Shelves</h4>
      {place.shelves.map((s) => (
        <ShelfRow key={s.id} id={s.id} name={s.name} act={act} />
      ))}
      <div className="row" style={{ marginTop: 8 }}>
        <input className="grow" value={newShelf} onChange={(e) => setNewShelf(e.target.value)} placeholder="New shelf name" />
        <button className="btn" disabled={!newShelf.trim()} onClick={() => void act(() => post(`/api/places/${place.id}/shelves`, { name: newShelf }), 'Shelf added').then(() => setNewShelf(''))}>
          + Add shelf
        </button>
      </div>
      <Err e={err} />
    </Modal>
  );
}

function ShelfRow({ id, name, act }: { id: number; name: string; act: (fn: () => Promise<unknown>, msg: string) => Promise<void> }) {
  const [v, setV] = useState(name);
  return (
    <div className="row shelfrow">
      <input className="grow" value={v} onChange={(e) => setV(e.target.value)} />
      <button className="btn small" disabled={!v.trim() || v === name} onClick={() => void act(() => patch(`/api/shelves/${id}`, { name: v }), 'Shelf renamed')}>
        Rename
      </button>
      <button className="btn small danger" onClick={() => confirm(`Delete shelf ${name}? Only possible when it is empty.`) && void act(() => del(`/api/shelves/${id}`), 'Shelf deleted')}>
        Delete
      </button>
    </div>
  );
}

export function Places() {
  const { d } = useApp();
  const contents = useContents();
  const [spot, setSpot] = useState<Spot | null>(null);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const editPlace = typeof editing === 'number' ? d.placeById.get(editing) : undefined;
  const noPlace = d.b.items.filter((i) => i.placeId == null && i.qty > 0).length;
  return (
    <section className="places">
      <div className="toolbar">
        <h2>Places</h2>
        <span className="meta">Click a shelf to see exactly what is on it.</span>
        <span className="grow" />
        {d.isAdmin && (
          <button className="btn" onClick={() => setEditing('new')}>
            + New place
          </button>
        )}
      </div>
      {!d.b.places.length && <p className="meta">No places yet.{d.isAdmin ? ' Add one with “+ New place”.' : ''}</p>}
      <div className="pgrid">
        {d.b.places.map((p) => {
          const loose = contents(p.id, null);
          return (
            <div key={p.id} className="pcard">
              <div className="phead">
                <b>{p.name}</b>
                {d.isAdmin && (
                  <button className="btn small" onClick={() => setEditing(p.id)}>
                    Edit
                  </button>
                )}
              </div>
              {p.shelves.map((s) => {
                const c = contents(p.id, s.id);
                return (
                  <button key={s.id} className={`shelfbtn${c.rows ? '' : ' empty'}`} onClick={() => setSpot({ place: p, shelfId: s.id, label: `${p.name} › ${s.name}` })}>
                    <span>{s.name}</span>
                    <span className="meta">{c.rows ? `${pl(c.rows, 'item')} · ${units(c.units)}` : 'empty'}</span>
                  </button>
                );
              })}
              {loose.rows > 0 && (
                <button className="shelfbtn" onClick={() => setSpot({ place: p, shelfId: null, label: `${p.name} (no shelf)` })}>
                  <span>
                    <i>No shelf</i>
                  </span>
                  <span className="meta">
                    {pl(loose.rows, 'item')} · {units(loose.units)}
                  </span>
                </button>
              )}
              {!p.shelves.length && !loose.rows && <p className="meta">No shelves.</p>}
            </div>
          );
        })}
      </div>
      {noPlace > 0 && <p className="meta">{pl(noPlace, 'item')} in the warehouse without a place.</p>}
      {spot && <ShelfDialog spot={spot} onClose={() => setSpot(null)} />}
      {(editing === 'new' || editPlace) && <PlaceEditor key={editing} place={editPlace} onClose={() => setEditing(null)} />}
    </section>
  );
}
